import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { ServiceConstant } from '../utils/service-constant.util';
import {
  BulkTableLayoutUpdate,
  Table,
  TableCombination,
  TableCombinationInput,
} from '../models/table';
import { FNFResponse } from '../models/response';

@Injectable({
  providedIn: 'root',
})
export class TableService {
  httpClient = inject(HttpClient);
  constructor() {}

  getAllTables(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/restaurants/tables').pipe(
      catchError((error) => {
        console.error('Error fetching tables:', error);
        return of(error);
      }),
    );
  }

  addNewTable(table: any): Observable<any> {
    return this.httpClient.post<any>(ServiceConstant.apiUrl + '/restaurants/tables', table).pipe(
      catchError((error) => {
        console.error('Error posting tables:', error);
        return of(error);
      }),
    );
  }

  editTable(table: any): Observable<any> {
    return this.httpClient
      .put<any>(ServiceConstant.apiUrl + '/restaurants/tables/' + table.table_id, table)
      .pipe(
        catchError((error) => {
          console.error('Error putting tables:', error);
          return of(error);
        }),
      );
  }

  deleteTable(tableId: any): Observable<any> {
    return this.httpClient
      .delete<any>(ServiceConstant.apiUrl + '/restaurants/tables/' + tableId)
      .pipe(
        catchError((error) => {
          console.error('Error deleteing tables:', error);
          return of(error);
        }),
      );
  }

  editTablePatch(table: any): Observable<any> {
    return this.httpClient
      .patch<any>(ServiceConstant.apiUrl + '/restaurants/tables/' + table.table_id, table)
      .pipe(
        catchError((error) => {
          console.error('Error patching tables:', error);
          return of(error);
        }),
      );
  }

  /**
   * Saves floor-plan positions for many tables at once. Errors are NOT
   * swallowed here (unlike the methods above) because the caller needs the
   * 409 status to tell a version conflict from any other failure.
   */
  saveTableLayout(payload: BulkTableLayoutUpdate): Observable<FNFResponse<Table[]>> {
    return this.httpClient.put<FNFResponse<Table[]>>(
      ServiceConstant.apiUrl + '/restaurants/tables/layout',
      payload,
    );
  }

  // ------------------------------------------------ combinations (FNF-1184)
  // Unlike the calls above, these throw on failure, so the page can say what
  // went wrong.

  private readonly combinationsUrl = ServiceConstant.apiUrl + '/restaurants/tables/combinations';

  listCombinations(): Observable<TableCombination[]> {
    return this.httpClient
      .get<FNFResponse<{ combinations: TableCombination[] }>>(this.combinationsUrl)
      .pipe(map((res) => res?.data?.combinations ?? []));
  }

  createCombination(input: TableCombinationInput): Observable<TableCombination> {
    return this.httpClient
      .post<FNFResponse<TableCombination>>(this.combinationsUrl, input)
      .pipe(map((res) => this.dataOrThrow(res)));
  }

  updateCombination(id: string, input: TableCombinationInput): Observable<TableCombination> {
    return this.httpClient
      .put<FNFResponse<TableCombination>>(`${this.combinationsUrl}/${id}`, input)
      .pipe(map((res) => this.dataOrThrow(res)));
  }

  deleteCombination(id: string): Observable<void> {
    return this.httpClient
      .delete<FNFResponse<unknown>>(`${this.combinationsUrl}/${id}`)
      .pipe(map(() => undefined));
  }

  private dataOrThrow<T>(res: FNFResponse<T> | null): T {
    if (!res?.data) {
      throw new Error(res?.error?.message || 'The combination was not saved.');
    }
    return res.data;
  }
}
